// KeyInfo.js
import React, { useState } from "react";
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, ButtonBase, Collapse,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import HomeIcon from "@mui/icons-material/Home";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ElderlyIcon from "@mui/icons-material/Elderly";
import SchoolIcon from "@mui/icons-material/School";

// ─── Design tokens (matches Sidebar light theme) ──────────────────────────────
const T = {
  bg: "#ffffff",
  surface: "#f8f9fc",
  surfaceHover: "#f0f2f7",
  border: "rgba(0,0,0,0.07)",
  borderStrong: "rgba(0,0,0,0.13)",
  text: "#1a1d23",
  textMuted: "#6b7280",
  textFaint: "#9ca3af",
  accent: "#2563eb",
  accentDim: "rgba(37,99,235,0.08)",
  accentGlow: "rgba(37,99,235,0.04)",
  chip: "#f3f4f6",
  chipActive: "rgba(37,99,235,0.1)",
  chipActiveBorder: "rgba(37,99,235,0.35)",
  up: "#16a34a",
  down: "#dc2626",
  rep: "#dc2626",
  dem: "#2563eb",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toTitleCase(str) {
  return str.toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

const partyColor = (party) => {
  if (!party) return T.text;
  if (party === "R" || party === "Republican") return T.rep;
  if (party === "D" || party === "Democrat" || party === "Democratic") return T.dem;
  return T.textMuted;
};

const candidateColor = (name) => {
  if (name === "Trump") return T.rep;
  if (name === "Harris" || name === "Biden") return T.dem;
  return T.textMuted;
};

// ─── Shared UI primitives ─────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: "10px", fontWeight: 700, color: T.textFaint,
    letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif",
  }}>
    {children}
  </Typography>
);

const AreaTitle = ({ bold, light }) => (
  <Box sx={{ mb: 1.5 }}>
    {bold && (
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>
        {bold}
        {light && (
          <Box component="span" sx={{ fontWeight: 400, color: T.textMuted, ml: 0.75, fontSize: "12px" }}>
            ({light})
          </Box>
        )}
      </Typography>
    )}
    {!bold && light && (
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>
        {light}
      </Typography>
    )}
  </Box>
);

// An election result row: "2024 · Trump 49.8% Harris 48.3%"
const ElectionRow = ({ year, candidates }) => (
  <Box sx={{ mb: 0.5 }}>
    <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, fontFamily: "'DM Sans', sans-serif", minWidth: 28 }}>
        {year}
      </Typography>
      {candidates.map((c, i) => (
        <Typography key={i} sx={{
          fontSize: "12px", fontWeight: i === 0 ? 700 : 400,
          color: candidateColor(c.name), fontFamily: "'DM Sans', sans-serif",
        }}>
          {c.name}: {(c.value * 100).toFixed(1)}%
        </Typography>
      ))}
    </Stack>
  </Box>
);

// A politician row: "Sen · Smith (D) · Next: 2026"
const PoliticianRow = ({ icon: Icon, iconColor, label, name, party, nextElex }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.75 }}>
    <Box sx={{ color: iconColor || T.textFaint, mt: "2px", flexShrink: 0 }}>
      <Icon sx={{ fontSize: 14 }} />
    </Box>
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>{label}:</Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: partyColor(party), fontFamily: "'DM Sans', sans-serif" }}>
          {name} ({party})
        </Typography>
        {nextElex && (
          <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
            · next: {nextElex}
          </Typography>
        )}
      </Stack>
    </Box>
  </Stack>
);

// ─── Stat tile (Demographics grid) ───────────────────────────────────────────
const StatTile = ({ icon: Icon, iconColor, label, value, comparison, isHigher }) => (
  <Box sx={{
    p: 1.25, borderRadius: "9px", bgcolor: T.surface,
    border: `1px solid ${T.border}`, textAlign: "center",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25,
  }}>
    <Box sx={{ color: iconColor, mb: 0.25 }}>
      <Icon sx={{ fontSize: 18 }} />
    </Box>
    <Typography sx={{ fontSize: "10px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Typography sx={{
      fontSize: "14px", fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
      color: isHigher === null ? T.text : (isHigher ? T.up : T.down),
    }}>
      {value}
    </Typography>
    {comparison != null && (
      <Typography sx={{ fontSize: "10px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
        ({comparison})
      </Typography>
    )}
  </Box>
);

// ─── Ethnicity table ──────────────────────────────────────────────────────────
const EthnicityTable = ({ row, benchmarkRow }) => {
  const cols = [
    { key: "ethnicity_white", label: "White" },
    { key: "ethnicity_black", label: "Black" },
    { key: "ethnicity_hispanic", label: "Hispanic" },
    { key: "ethnicity_asian", label: "Asian" },
    { key: "ethnicity_other", label: "Other" },
  ];
  return (
    <Box sx={{ mt: 1.5, borderRadius: "9px", border: `1px solid ${T.border}`, overflow: "hidden" }}>
      {/* Header row */}
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cols.length}, 1fr)`, bgcolor: T.surface }}>
        {cols.map((c) => (
          <Box key={c.key} sx={{ py: 0.6, textAlign: "center", borderRight: `1px solid ${T.border}`, "&:last-child": { borderRight: "none" } }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              {c.label}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* Value row */}
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cols.length}, 1fr)`, bgcolor: T.bg }}>
        {cols.map((c) => {
          const val = row ? Number(row[c.key]) : null;
          const bench = benchmarkRow ? Number(benchmarkRow[c.key]) : null;
          const isHigher = (val != null && bench != null) ? val > bench : null;
          return (
            <Box key={c.key} sx={{ py: 0.75, textAlign: "center", borderRight: `1px solid ${T.border}`, "&:last-child": { borderRight: "none" } }}>
              <Typography sx={{
                fontSize: "13px", fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                color: isHigher === null ? T.text : (isHigher ? T.up : T.down),
              }}>
                {val != null ? (val * 100).toFixed(0) + "%" : "—"}
              </Typography>
              {benchmarkRow && (
                <Typography sx={{ fontSize: "10px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                  ({bench != null ? (bench * 100).toFixed(0) + "%" : "—"})
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── Political sub-components ─────────────────────────────────────────────────
const StateLevelPoliComponent = ({ candidates, filtered, house }) => {
  if (!candidates?.length || !filtered?.length || !house?.length) return null;
  const f = filtered[0];
  return (
    <Stack spacing={1.5}>
      {/* Presidential */}
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>Presidential</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <ElectionRow year="2024" candidates={candidates} />
          {f.pres_2024_totalvote != null && (
            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
              {Number(f.pres_2024_totalvote).toLocaleString()} total votes
            </Typography>
          )}
        </Box>
      </Box>

      {/* Senators */}
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>U.S. Senate</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <PoliticianRow icon={AccountBalanceIcon} iconColor="#f59e0b" label="Senior" name={f.senior_senator} party={f.senior_senator_party} nextElex={f.senior_senator_next_elex} />
          <PoliticianRow icon={AccountBalanceIcon} iconColor="#f59e0b" label="Junior" name={f.junior_senator} party={f.junior_senator_party} nextElex={f.junior_senator_next_elex} />
        </Box>
      </Box>

      {/* House delegation */}
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>House Delegation</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          {house.map((h, i) => (
            <Typography key={i} sx={{ fontSize: "12px", fontFamily: "'DM Sans', sans-serif", color: partyColor(h.name), fontWeight: i === 0 ? 700 : 400 }}>
              {h.value} {h.name}{i < house.length - 1 ? "," : ""}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Governor / State legislature */}
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>State Government</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <PoliticianRow icon={AccountBalanceIcon} iconColor="#9ca3af" label="Governor" name={f.governor_name} party={f.governor_party} nextElex={f.governor_next_elex} />
          <PoliticianRow icon={AccountBalanceIcon} iconColor="#9ca3af" label="Lt. Governor" name={f.lt_governor_name} party={f.lt_governor_party} nextElex={f.lt_governor_next_elex} />
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
              Upper house: <Box component="span" sx={{ fontWeight: 600, color: partyColor(f.state_upper_house_control) }}>{f.state_upper_house_control}</Box>
              {f.state_upper_house_next_election && <Box component="span" sx={{ color: T.textFaint }}> · next: {f.state_upper_house_next_election}</Box>}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} flexWrap="wrap">
            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
              Lower house: <Box component="span" sx={{ fontWeight: 600, color: partyColor(f.state_lower_house_control) }}>{f.state_lower_house_control}</Box>
              {f.state_lower_house_nextelex && <Box component="span" sx={{ color: T.textFaint }}> · next: {f.state_lower_house_nextelex}</Box>}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
};

const CountyLevelPoliComponent = ({ candidates20, candidates, filtered, geoLevel }) => {
  if (!candidates?.length || !filtered?.length) return null;
  if (geoLevel !== "zipcodes" &&geoLevel !== "media_market" && !candidates20?.length) return null;
  const f = filtered[0];
  return (
    <Stack spacing={1.5}>
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>Presidential</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <ElectionRow year="2024" candidates={candidates} />
          {f.pres_2024_totalvote != null && (
            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
              {Number(f.pres_2024_totalvote).toLocaleString('en-US', { maximumFractionDigits: 0 })} total votes
            </Typography>
          )}
          {candidates20 && (
            <>
              <Box sx={{ height: "1px", bgcolor: T.border, my: 0.75 }} />
              <ElectionRow year="2020" candidates={candidates20} />
              {f.pres_2020_totalvote != null && (
                <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                  {Number(f.pres_2020_totalvote).toLocaleString('en-US', { maximumFractionDigits: 0 })} total votes
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Stack>
  );
};
const CDLevelPoliComponent = ({ candidates20, candidates, filtered, house }) => {
  if (!candidates?.length || !filtered?.length || !candidates20?.length || !house?.length) return null;
  const f = filtered[0];
  return (
    <Stack spacing={1.5}>
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>House Race</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <PoliticianRow icon={AccountBalanceIcon} iconColor="#9ca3af" label="Incumbent" name={f.house_2024_incumbent} party={f.house_2024_party} />
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {house.map((h, i) => (
              <Typography key={i} sx={{ fontSize: "12px", fontFamily: "'DM Sans', sans-serif", color: partyColor(h.name), fontWeight: i === 0 ? 700 : 400 }}>
                {(h.value * 100).toFixed(1)}% {h.name}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Box>
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>Presidential</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <ElectionRow year="2024" candidates={candidates} />
          <Box sx={{ height: "1px", bgcolor: T.border, my: 0.75 }} />
          <ElectionRow year="2020" candidates={candidates20} />
        </Box>
      </Box>
    </Stack>
  );
};

const StateLegPoliComponent = ({ candidates, filtered }) => {
  if (!candidates?.length || !filtered?.length) return null;
  const f = filtered[0];
  return (
    <Stack spacing={1.5}>
      <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
        <SectionLabel>Presidential (District)</SectionLabel>
        <Box sx={{ mt: 0.75 }}>
          <ElectionRow year="2024" candidates={candidates} />
          {f?.pres_2024_totalvote != null && (
            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
              {Number(f.pres_2024_totalvote).toFixed(0).toLocaleString()} total votes
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

const NationalPoliComponent = () => (
  <Stack spacing={1.5}>
    <Box sx={{ p: 1.25, borderRadius: "9px", bgcolor: T.surface, border: `1px solid ${T.border}` }}>
      <SectionLabel>Presidential</SectionLabel>
      <Box sx={{ mt: 0.75 }}>
        <ElectionRow year="2024" candidates={[{ name: "Trump", value: 0.498 }, { name: "Harris", value: 0.483 }]} />
        <Box sx={{ height: "1px", bgcolor: T.border, my: 0.75 }} />
        <ElectionRow year="2020" candidates={[{ name: "Biden", value: 0.513 }, { name: "Trump", value: 0.468 }]} />
      </Box>
    </Box>
  </Stack>
);

const OuterPoliComponent = ({ filtered, geoLevel }) => {
  const trump = filtered?.[0]?.pres_2024_trump ?? null;
  const harris = filtered?.[0]?.pres_2024_harris ?? null;
  const trump20 = filtered?.[0]?.pres_2020_trump ?? null;
  const biden20 = filtered?.[0]?.pres_2020_biden ?? null;
  const house_d = filtered?.[0]?.house_2024_dem ?? null;
  const house_r = filtered?.[0]?.house_2024_rep ?? null;

  const candidates = [{ name: "Trump", value: trump }, { name: "Harris", value: harris }].sort((a, b) => b.value - a.value);
  const candidates20 = [{ name: "Trump", value: trump20 }, { name: "Biden", value: biden20 }].sort((a, b) => b.value - a.value);
  const house = [{ name: "Republican", value: house_r }, { name: "Democrat", value: house_d }].sort((a, b) => b.value - a.value);

  if (geoLevel === "states") {
    return (
      <>
        {filtered?.[0]?.state && <AreaTitle bold={filtered[0].state} />}
        <StateLevelPoliComponent candidates={candidates} filtered={filtered} house={house} />
      </>
    );
  }
  if (geoLevel === "counties") {
    return (
      <>
        {filtered?.[0]?.county_name && <AreaTitle bold={filtered[0].county_name} />}
        <CountyLevelPoliComponent candidates20={candidates20} candidates={candidates} filtered={filtered} geoLevel={geoLevel} />
      </>
    );
  }
  if (geoLevel === "cd") {
    const stateName = filtered?.[0]?.state ? toTitleCase(filtered[0].state) : "";
    const distStr = filtered?.[0]?.area_id ? String(filtered[0].area_id).substring(2, 4) : "";
    return (
      <>
        <AreaTitle bold={`${stateName} ${distStr}`} />
        <CDLevelPoliComponent candidates20={candidates20} candidates={candidates} filtered={filtered} house={house} />
      </>
    );
  }
  if (geoLevel === "zipcodes"||geoLevel=="media_market") {
    return (
      <>
        {filtered?.[0]?.area_id && <AreaTitle bold={String(filtered[0].area_id)} />}
        <CountyLevelPoliComponent candidates20={null} candidates={candidates} filtered={filtered} geoLevel={geoLevel} />
      </>
    );
  }
  if (geoLevel === "statehouse" || geoLevel === "statesenate") {
    return (
      <>
        {filtered?.[0]?.name && <AreaTitle bold={filtered[0].name} />}
        <StateLegPoliComponent candidates={candidates} filtered={filtered} geoLevel={geoLevel} />
      </>
    );
  }
  return (
    <>
      <AreaTitle bold="National" />
      <NationalPoliComponent />
    </>
  );
};

// ─── Demographics content ─────────────────────────────────────────────────────
// Mirrors the original's three branches exactly:
//   Branch 1: filtered.length > 0 && fixed_top_level  → hovered stats WITH green/red comparison to benchmark
//   Branch 2: filtered.length === 0 && fixed_top_level → fixed_top_level stats only, NO comparison coloring, NO benchmark row in ethnicity table
//   Branch 3: fallback → "Loading"
export const DemographicsContent = ({ filtered, fixed_top_level, demogs_name_bold, demogs_name_lower }) => {
  const hasFiltered = filtered && filtered.length > 0;

  // Branch 3: Loading
  if (!fixed_top_level) {
    return (
      <Typography sx={{ fontSize: "12px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
        Loading…
      </Typography>
    );
  }

  // Branch 1: hovered area exists — show hovered data with green/red vs benchmark
  if (hasFiltered) {
    const f = filtered[0];
    const b = fixed_top_level[0];

    const isHigher = (field) => {
      const a = Number(f[field]);
      const bv = Number(b?.[field]);
      if (isNaN(a) || isNaN(bv)) return false;
      return a > bv;
    };

    const statTiles = [
      {
        icon: ElderlyIcon, iconColor: "#a855f7", label: "Median age",
        value: f.median_age != null ? Number(f.median_age.toFixed(0)) : "—",
        comparison: b?.median_age != null ? Number(b.median_age.toFixed(0)) : null,
        isHigher: f.median_age != null && b?.median_age != null ? isHigher("median_age") : null,
      },
      {
        icon: AttachMoneyIcon, iconColor: "#16a34a", label: "Median income",
        value: f.median_income != null ? `$${Number(f.median_income.toFixed(0)).toLocaleString()}` : "—",
        comparison: b?.median_income != null ? `$${Number(b.median_income.toFixed(0)).toLocaleString()}` : null,
        isHigher: f.median_income != null && b?.median_income != null ? isHigher("median_income") : null,
      },
      {
        icon: SchoolIcon, iconColor: "#2563eb", label: "Degree",
        value: f.education_degreeplus != null ? `${(Number(f.education_degreeplus) * 100).toFixed(0)}%` : "—",
        comparison: b?.education_degreeplus != null ? `${(Number(b.education_degreeplus) * 100).toFixed(0)}%` : null,
        isHigher: f.education_degreeplus != null && b?.education_degreeplus != null
          ? Number(f.education_degreeplus.toFixed(2)) > Number(b.education_degreeplus.toFixed(2)) : null,
      },
      {
        icon: MoneyOffIcon, iconColor: "#9ca3af", label: "In poverty",
        value: f.poverty_below_poverty_line != null ? `${(Number(f.poverty_below_poverty_line) * 100).toFixed(0)}%` : "—",
        comparison: b?.poverty_below_poverty_line != null ? `${(Number(b.poverty_below_poverty_line) * 100).toFixed(0)}%` : null,
        isHigher: f.poverty_below_poverty_line != null && b?.poverty_below_poverty_line != null
          ? Number(f.poverty_below_poverty_line.toFixed(2)) > Number(b.poverty_below_poverty_line.toFixed(2)) : null,
      },
      {
        icon: LocalHospitalIcon, iconColor: "#f59e0b", label: "Medicare/aid",
        value: f.healthcare_reliant_on_medicaid_medicare != null ? `${(Number(f.healthcare_reliant_on_medicaid_medicare) * 100).toFixed(0)}%` : "—",
        comparison: b?.healthcare_reliant_on_medicaid_medicare != null ? `${(Number(b.healthcare_reliant_on_medicaid_medicare) * 100).toFixed(0)}%` : null,
        isHigher: f.healthcare_reliant_on_medicaid_medicare != null && b?.healthcare_reliant_on_medicaid_medicare != null
          ? Number(f.healthcare_reliant_on_medicaid_medicare.toFixed(2)) > Number(b.healthcare_reliant_on_medicaid_medicare.toFixed(2)) : null,
      },
      {
        icon: HomeIcon, iconColor: "#ef4444", label: "Homeowners",
        value: f.tenure_own_home != null ? `${(Number(f.tenure_own_home) * 100).toFixed(0)}%` : "—",
        comparison: b?.tenure_own_home != null ? `${(Number(b.tenure_own_home) * 100).toFixed(0)}%` : null,
        isHigher: f.tenure_own_home != null && b?.tenure_own_home != null
          ? Number(f.tenure_own_home.toFixed(2)) > Number(b.tenure_own_home.toFixed(2)) : null,
      },
    ];

    return (
      <>
        <AreaTitle bold={demogs_name_bold} light={demogs_name_lower} />
        <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif", mb: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 600, color: T.up }}>Green</Box> / <Box component="span" sx={{ fontWeight: 600, color: T.down }}>red</Box> vs benchmark · parentheses show benchmark
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 0.5 }}>
          {statTiles.map((tile, i) => <StatTile key={i} {...tile} />)}
        </Box>
        <Box sx={{ mt: 1 }}>
          <SectionLabel>Ethnicity</SectionLabel>
          {/* Branch 1 ethnicity table: hovered row + benchmark row beneath */}
          <EthnicityTable row={f} benchmarkRow={b} />
        </Box>
      </>
    );
  }

  // Branch 2: no hovered area — show fixed_top_level only, NO comparison, NO benchmark row
  const f = fixed_top_level[0];
  const statTiles = [
    {
      icon: ElderlyIcon, iconColor: "#a855f7", label: "Median age",
      value: f?.median_age != null ? Number(f.median_age.toFixed(0)) : "—",
      comparison: null, isHigher: null,
    },
    {
      icon: AttachMoneyIcon, iconColor: "#16a34a", label: "Median income",
      value: f?.median_income != null ? `$${Number(f.median_income.toFixed(0)).toLocaleString()}` : "—",
      comparison: null, isHigher: null,
    },
    {
      icon: SchoolIcon, iconColor: "#2563eb", label: "Degree",
      value: f?.education_degreeplus != null ? `${(Number(f.education_degreeplus) * 100).toFixed(0)}%` : "—",
      comparison: null, isHigher: null,
    },
    {
      icon: MoneyOffIcon, iconColor: "#9ca3af", label: "In poverty",
      value: f?.poverty_below_poverty_line != null ? `${(Number(f.poverty_below_poverty_line) * 100).toFixed(0)}%` : "—",
      comparison: null, isHigher: null,
    },
    {
      icon: LocalHospitalIcon, iconColor: "#f59e0b", label: "Medicare/aid",
      value: f?.healthcare_reliant_on_medicaid_medicare != null ? `${(Number(f.healthcare_reliant_on_medicaid_medicare) * 100).toFixed(0)}%` : "—",
      comparison: null, isHigher: null,
    },
    {
      icon: HomeIcon, iconColor: "#ef4444", label: "Homeowners",
      value: f?.tenure_own_home != null ? `${(Number(f.tenure_own_home) * 100).toFixed(0)}%` : "—",
      comparison: null, isHigher: null,
    },
  ];

  return (
    <>
      {/* Branch 2 shows demogs_name_lower as bold (no paired name) */}
      <AreaTitle bold={demogs_name_lower} />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 0.5 }}>
        {statTiles.map((tile, i) => <StatTile key={i} {...tile} />)}
      </Box>
      <Box sx={{ mt: 1 }}>
        <SectionLabel>Ethnicity</SectionLabel>
        {/* Branch 2 ethnicity table: only one data row, no benchmark row */}
        <EthnicityTable row={f} benchmarkRow={null} />
      </Box>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const KeyInfo = ({ poliInfo, hoverArea, geoLevel, poliInfoToplevel, selectedAreaId, selectedFeature }) => {
  const [active, setActive] = React.useState(0);
  const bottomRef = React.useRef(null);

  const filtered = (poliInfo || []).filter((item) => item.area_id === hoverArea);

  // Auto-scroll only if the user is already near the bottom of the sidebar
  // (i.e. already looking at the KeyInfo section). Finds the nearest scrollable
  // ancestor and checks if the bottom sentinel is close to visible.
  React.useEffect(() => {
    if (!hoverArea || filtered.length === 0 || !bottomRef.current) return;

    // Walk up the DOM to find the scrollable sidebar container
    let scrollParent = bottomRef.current.parentElement;
    while (scrollParent && scrollParent !== document.body) {
      const { overflowY } = window.getComputedStyle(scrollParent);
      if (overflowY === "auto" || overflowY === "scroll") break;
      scrollParent = scrollParent.parentElement;
    }
    if (!scrollParent) return;

    // Only scroll if the bottom of the scrollable area is already within ~200px
    // of the bottom sentinel — meaning the user can already see KeyInfo
    const parentRect = scrollParent.getBoundingClientRect();
    const bottomRect = bottomRef.current.getBoundingClientRect();
    const distanceToBottom = bottomRect.bottom - parentRect.bottom;

    if (distanceToBottom > 0 && distanceToBottom < 200) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [hoverArea]);

  // Benchmark logic:
  // - For sub-state geo levels (counties, CDs, zipcodes, state leg districts), always compare
  //   the hovered area against its parent STATE, looked up by the hovered row's `state` field.
  // - For state-level or national, fall back to poliInfoToplevel (national benchmark).
  const subStateLevels = ["counties", "cd", "zipcodes", "statehouse", "statesenate"];
  const isSubState = subStateLevels.includes(geoLevel);

  let fixed_top_level;
  if (isSubState && filtered.length > 0) {
    // Primary: extract the 2-digit state FIPS from the hovered area_id (works for counties=5-digit, CDs=4-digit, etc.)
    const areaIdStr = filtered[0].area_id?.toString() ?? "";
    const stateFips = areaIdStr.length >= 2 ? areaIdStr.substring(0, 2) : null;
    const stateByFips = stateFips
      ? (poliInfo || []).filter((item) => item.area_id?.toString() === stateFips)
      : [];
    // Fallback: match by state name string
    const stateByName = filtered[0].state
      ? (poliInfo || []).filter(
          (item) => item.name?.toLowerCase() === filtered[0].state?.toLowerCase()
            && item.area_id?.toString().length === 2
        )
      : [];
    const stateRow = stateByFips.length > 0 ? stateByFips : stateByName;
    fixed_top_level = stateRow.length > 0 ? stateRow : (poliInfoToplevel ?? null);
  } else if (selectedAreaId === null) {
    fixed_top_level = poliInfoToplevel ?? null;
  } else if (Number(selectedAreaId) === Number(poliInfoToplevel?.[0]?.area_id ?? null)) {
    fixed_top_level = poliInfoToplevel ?? null;
  } else {
    fixed_top_level = (poliInfo || []).filter((item) => item.area_id === selectedAreaId);
  }

  // demogs_name_lower labels the benchmark in parentheses
  let demogs_name_lower;
  if (isSubState && fixed_top_level?.length > 0) {
    // Always show the state name as the benchmark label for sub-state areas
    demogs_name_lower = fixed_top_level[0].name ?? fixed_top_level[0].state ?? null;
  } else {
    switch (selectedFeature?.level) {
      case "cd":
        demogs_name_lower = fixed_top_level?.length > 0
          ? toTitleCase(fixed_top_level[0]?.state) + " " + String(fixed_top_level[0]?.area_id).substring(2, 4)
          : null;
        break;
      default:
        demogs_name_lower = fixed_top_level?.length > 0 ? fixed_top_level[0].name : null;
    }
  }

  let demogs_name_bold;
  switch (geoLevel) {
    case "states": demogs_name_bold = filtered?.length > 0 ? filtered[0].name : null; break;
    case "cd": demogs_name_bold = filtered?.length > 0 && filtered[0].state
      ? toTitleCase(filtered[0].state) + " " + String(filtered[0].area_id).substring(2, 4) : null; break;
    case "counties": demogs_name_bold = filtered?.length > 0 ? filtered[0].county_name : null; break;
    case "zipcodes": demogs_name_bold = filtered?.length > 0 ? filtered[0].area_id : null; break;
    default: demogs_name_bold = "";
  }

  const tabs = [
    { label: "Demographics" },
    { label: "Politics" },
  ];

  return (
    <Box sx={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Tab bar */}
      <Box sx={{ display: "flex", gap: 0.5, mb: 1.5 }}>
        {tabs.map(({ label }, i) => (
          <ButtonBase
            key={i}
            disableRipple
            onClick={() => setActive(i)}
            sx={{
              flex: 1, py: 0.75, px: 1,
              borderRadius: "8px",
              border: "1px solid",
              borderColor: active === i ? T.chipActiveBorder : T.border,
              bgcolor: active === i ? T.chipActive : T.chip,
              color: active === i ? T.accent : T.textMuted,
              fontSize: "12px",
              fontWeight: active === i ? 700 : 400,
              fontFamily: "'DM Sans', sans-serif",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: active === i ? T.chipActiveBorder : T.borderStrong,
                bgcolor: active === i ? T.chipActive : T.surfaceHover,
                color: active === i ? T.accent : T.text,
              },
              "&:focus": { outline: "none" },
            }}
          >
            {label}
          </ButtonBase>
        ))}
      </Box>

      {/* Content */}
      {filtered && fixed_top_level && (
        <Box>
          {active === 0 && (
            <DemographicsContent
              filtered={filtered}
              fixed_top_level={fixed_top_level}
              demogs_name_bold={demogs_name_bold}
              demogs_name_lower={demogs_name_lower}
            />
          )}

          {active === 1 && (
            <>
              {filtered.length > 0 && filtered[0].pres_2024_harris != null ? (
                <OuterPoliComponent filtered={filtered} geoLevel={geoLevel} />
              ) : (
                <OuterPoliComponent
                  filtered={fixed_top_level}
                  geoLevel={selectedFeature ? selectedFeature.level : "national"}
                />
              )}
            </>
          )}
          {/* Scroll anchor — sits at the bottom of the content so scrollIntoView reveals everything */}
          <Box ref={bottomRef} sx={{ height: 1 }} />
        </Box>
      )}
    </Box>
  );
};

export default KeyInfo;
